import NavUrlRenderer from '@alme/ui-web/modules/message-renderer-nav-url/src/message-renderer-nav-url';
import { sendRequest } from '../../node_modules/@alme/ui-web/modules/lib-alme-request/src/lib-alme-request';

import { NavUrl } from "@alme/types/response/NavUrl";
import { Input } from '@alme/ui-web/types/input'
import { EntryOriginTypesV5 } from '@alme/api-ts/Alme/EntryOriginTypes';
import { AlightCustomSettingsType } from '../../settings/alight-custom-settings';
import { VChild, h } from 'virtual-dom';
import { HistoryMessage, HistoryMessageAlme } from '@alme/ui-web/types/history';
import { Dictionary } from '@alme/ui-web/types/rendering';
import objectPath from 'object-path';
import { shouldNav } from '@alme/ui-web/util/dnn';
import { getAlightLinkDetails } from '../lib-alight-library/lib-alight-library';

/// Custom Display Nav Url - Extends base, but overrides URL for warning message
export default class CustomNavUrlRenderer extends NavUrlRenderer {

    getRenderer() {
        return (message: HistoryMessage): VChild => {
            if (!this.doesRender(message)) { return []; }

            const navUrl = (message as HistoryMessageAlme).navUrl as NavUrl;
            const isPopupLink = navUrl.LinkNavigationType === "Popup";
            const shouldNavigate: boolean = shouldNav(this.getWindowLocation(), this._settings);
            const enableCurrentPageMatching = this._settings.navConstants.enableCurrentPageMatching;
            const entryId = message?.extensionData?.entryId as string;
            const alightSettings = this._settings as AlightCustomSettingsType;
            if (navUrl?.Metadata?.onClickFunctionString) {
                if (document.getElementById(`programlink-${entryId}`)) {
                    document.getElementById(`programlink-${entryId}`)?.setAttribute("onclick", navUrl?.Metadata?.onClickFunctionString.toString());
                }
            }
            else {
                const linkObj = getAlightLinkDetails(navUrl.UrlAddress);
                if (alightSettings.configurationType.includes(linkObj.type) && window?.ivaRouting) {
                    if (document.getElementById(`programlink-${entryId}`)) {
                        document.getElementById(`programlink-${entryId}`)?.setAttribute("href", "javascript:void(0)");
                    }
                }
            }
            let properties: Dictionary =
            {
                className: this._settings.classNames.navLinkMainPage,
                href: shouldNavigate ? navUrl.UrlAddress : '',
                target: isPopupLink ? "_blank" : "_self",
                title: objectPath.get(navUrl, this._settings.navConstants.hoverTextPath) ?? "",
                onclick: (e: Event): boolean => {
                    if (enableCurrentPageMatching && this.linkUrlMatchesCurrentPage(navUrl.UrlAddress)) {
                        const popper = (e.target as HTMLElement)?.previousSibling as HTMLElement;
                        const responseText = popper?.previousSibling as HTMLElement;
                        if (popper && responseText) {
                            const spanHeight = responseText.clientHeight ?? popper.parentElement?.clientHeight;
                            popper.style.display = (popper.style.display === "none") ? "block" : "none";
                            popper.style.top = `${spanHeight - this._settings.Popper.responseOffset}px`;
                        }
                    }
                    else {
                        if (this.isDownloadlink(navUrl)) {
                            this.urlOnClickReporting(navUrl);
                            return true;
                        }
                        else {
                            this.urlOnClick(navUrl, isPopupLink);
                        }
                    }

                    e.stopPropagation();

                    return false;
                },
                onblur: (e: Event): void => {
                    const popper = (e.target as HTMLElement)?.previousSibling as HTMLElement;
                    if (popper?.style) {
                        popper.style.display = "none";
                    }
                },
            };
            if (entryId != null) {
                properties.id = `programlink-${entryId}`;
            }
            if (navUrl.DisplayText === "") {
                properties["className"] = this._settings.classNames.navLinkMainPageHidden;
            }

            if (this.shouldDisable()) {
                properties.href = '/';
                properties.onclick = (): boolean => false;
                properties.tabIndex = -1;
            }
            if (navUrl?.Metadata?.onClickFunctionString) {
                delete properties.onclick;
            }
            if (this.isDownloadlink(navUrl)) {
                properties.download = navUrl.Metadata?.Download
            }
            // run mutators to update properties
            properties = this._mutators.reduce((props, mutate) => mutate(props), properties);

            const friendlyLinkName = (navUrl.Metadata?.UrlName as string) ?? navUrl.DisplayText;
            const linkText = shouldNavigate ? navUrl.DisplayText : friendlyLinkName;
            const stepOutIconNode = this._stepOutIcon?.renderIcon(navUrl.UrlAddress);

            return [this.createPopper(), h('a', properties, stepOutIconNode ? [linkText, stepOutIconNode] : linkText)];
        };
    }
    //Override onclick
    urlOnClick(navUrl: NavUrl, isPopup: boolean): void {
        this.minimizeAgentInMobileView();

        this.doHypeCheck(navUrl).then(
            (updatedNavUrl) => {
                if (window.hasOwnProperty('genWarningPopup')) { //  checking to make sure genWarningPopup exists on this UPOINT page
                    if (window.genWarningPopup == 'Y') {
                        //  call UPOINT provided function and pass it the link
                        let autoNavTypes = this._settings.navConstants.autoNavTypes;
                        if (navUrl.AutoNavigationType == autoNavTypes.popup) {
                            this.doNavLogic(updatedNavUrl, isPopup);
                        }
                        else {
                            if (window.genericWarningPopup({ href: updatedNavUrl.UrlAddress })) {
                                this.doNavLogic(updatedNavUrl, isPopup);
                            }
                        }
                    }
                    else {
                        this.doNavLogic(updatedNavUrl, isPopup);
                    }
                }
                else {
                    this.doNavLogic(updatedNavUrl, isPopup);
                }
            }
        )
    }

    doNavLogic(navUrl: NavUrl, isPopup: boolean) {
        const input: Input =
        {
            eventName: 'UrlClicked',
            eventValue: navUrl.UrlAddress,
            messageType: this._settings.messageConstants.reportingEventRequest,
        };

        const navPromise = sendRequest(input);

        const alightSettings = this._settings as AlightCustomSettingsType;

        const linkObj = getAlightLinkDetails(navUrl.UrlAddress);

        if (alightSettings.configurationType.includes(linkObj.type) && window?.ivaRouting) {
            window?.ivaRouting(linkObj.type, linkObj.source, linkObj.name, linkObj.gnrc1, linkObj.gnrc2);
        }
        else {
            navPromise.then(() => {
                if (!isPopup) {
                    this.setWindowLocationHref(navUrl.UrlAddress);
                }
            });

            if (isPopup) {
                const newWin = window.open(navUrl.UrlAddress, '_blank');
                newWin?.focus();
            }
        }
    }

    async doHypeCheck(navUrl: NavUrl): Promise<NavUrl> {
        try {
            const alightSettings = this._settings as AlightCustomSettingsType;

            const hypeOfferParamName = "hype_offer";
            const url = new URL(navUrl.UrlAddress);

            const parsedHash = new URLSearchParams(
                url.hash.substring(url.hash.indexOf("?") + 1)
            );

            if (url.searchParams.has(hypeOfferParamName) || parsedHash.has(hypeOfferParamName)) {
                const eventParams = alightSettings.AppEvents.hypeOfferClicked.parameters;

                const input: Input =
                {
                    messageType: this._settings.messageConstants.appeventRequest,
                    eventName: alightSettings.AppEvents.hypeOfferClicked.eventName,
                    origin: this._settings.originConstants.internal as EntryOriginTypesV5,
                    parameters: {
                        [eventParams.offerId]: parsedHash.has(hypeOfferParamName) ? parsedHash.get(hypeOfferParamName) : url.searchParams.get(hypeOfferParamName),
                    },
                };

                await sendRequest(input);

                if (parsedHash.has(hypeOfferParamName)) {
                    parsedHash.delete(hypeOfferParamName);
                    const strippedHash = url.hash.substring(0, url.hash.indexOf("?"));
                    url.hash = strippedHash + "?" + parsedHash.toString();
                }
                else {
                    url.searchParams.delete(hypeOfferParamName);
                }

                navUrl.UrlAddress = url.toString();

                return Promise.resolve(navUrl);
            }


            return Promise.resolve(navUrl);
        }
        catch {
            //We do not want to block URL handling if we run into issues on Hype Check
            return Promise.resolve(navUrl);
        }
    }
}


// WEBPACK FOOTER //
// ./modules/custom-message-renderer-nav-url/custom-message-renderer-nav-url.ts